import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { IndianRupee, Users, TrendingUp } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { supabase } from "@/integrations/supabase/client";

interface PaymentRecord {
  id: string;
  user_name: string;
  email: string;
  phone: string;
  razorpay_payment_id: string | null;
  final_price: number;
  payment_status: string;
  created_at: string;
}

const AdminPayments = () => {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPayments = async () => {
      const { data, error } = await supabase
        .from("payments")
        .select("id, user_name, email, phone, razorpay_payment_id, final_price, payment_status, created_at")
        .order("created_at", { ascending: false });

      if (!error && data) {
        setPayments(data);
      }
      setLoading(false);
    };
    fetchPayments();
  }, []);

  const successfulPayments = payments.filter(
    (p) => p.payment_status === "success"
  );
  const totalRevenue = successfulPayments.reduce(
    (sum, p) => sum + (p.final_price || 0),
    0
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-6xl mx-auto">
          <WeKitLogo size="sm" />
          <Badge variant="outline">Admin</Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-display text-2xl font-bold text-foreground">
            Payment Dashboard
          </h1>
          <div className="flex items-center gap-4">
            <a
              href="/admin/payment-console"
              className="text-sm font-medium text-primary hover:underline"
            >
              Open payment console →
            </a>
            <a
              href="/admin/coupons"
              className="text-sm font-medium text-primary hover:underline"
            >
              Manage NGO coupons →
            </a>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Payments</p>
                  <p className="text-2xl font-bold text-foreground">
                    {payments.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <IndianRupee className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Revenue</p>
                  <p className="text-2xl font-bold text-foreground">
                    ₹{totalRevenue.toLocaleString("en-IN")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Students</p>
                  <p className="text-2xl font-bold text-foreground">
                    {successfulPayments.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Recent Payments</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-muted-foreground text-sm">Loading...</p>
            ) : payments.length === 0 ? (
              <p className="text-muted-foreground text-sm">No payments yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Payment ID</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium text-foreground">
                          {p.user_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.email}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs">
                          {p.razorpay_payment_id || "—"}
                        </TableCell>
                        <TableCell className="text-foreground">
                          ₹{p.final_price}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              p.payment_status === "success"
                                ? "default"
                                : p.payment_status === "pending"
                                ? "secondary"
                                : "destructive"
                            }
                            className="text-xs"
                          >
                            {p.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(p.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminPayments;
